import {
  ClearScheduleAction,
  SaveContextualSchedulerAction,
  SaveStepScheduleAction,
  UpdateRuleAction,
  UpdateStepAction,
  UpsertScheduleAction,
} from 'store/actionTypes';
import { getStepRuleSyntheticId } from 'store/helper';
import { selectStepRule } from 'store/rule/rule.selector';
import { RootState } from 'store/store';
import {
  ScheduleDraftAction,
  ScheduleRuleDraft,
  ScheduleSidePanelDraft,
  ScheduleStepDraft,
} from './scheduleDraft.types';

// Keep in sync with rule.reducer.ts (upsertSchedule, clearSchedule, saveContextualScheduler)
// and journey.reducer.ts (updateStep).

export function createEmptyDraft(): ScheduleSidePanelDraft {
  return {
    step: {},
    rule: {},
  };
}

export function seedDraftFromState(state: RootState, stepIdx: number): ScheduleSidePanelDraft {
  const synthId = getStepRuleSyntheticId(stepIdx);
  const step = state.te.journey.steps[stepIdx];
  const rule = selectStepRule(state.te, stepIdx);
  const startScheduler = state.te.rule.startSchedulerMap[synthId];
  const endScheduler = state.te.rule.endSchedulerMap[synthId];

  const stepDraft: ScheduleStepDraft = {
    omitSched: step?.omitSched,
    omitStart: step?.omitStart,
    omitEnd: step?.omitEnd,
    operationType: step?.operationType,
    scheduleBasedOn:
      Object.keys(startScheduler?.predicates || {}).length > 0
        ? 'events'
        : startScheduler?.cronExpression || startScheduler?.start
        ? 'time'
        : undefined,
  };

  const ruleDraft: ScheduleRuleDraft = {
    refId: rule?.refId,
    evaluateAtAbsoluteIntervalStart: rule?.evaluateAtAbsoluteIntervalStart,
  };

  return {
    step: stepDraft,
    startScheduler: startScheduler ? structuredClone(startScheduler) : undefined,
    endScheduler: endScheduler ? structuredClone(endScheduler) : undefined,
    rule: ruleDraft,
  };
}

function upsertScheduleDraft(draft: ScheduleSidePanelDraft, action: UpsertScheduleAction): ScheduleSidePanelDraft {
  const { type, startFields, endFields, ...sharedFields } = action.payload;
  const hasPerSidePatches = type === 'both' && (startFields || endFields);

  let { startScheduler, endScheduler } = draft;

  if (hasPerSidePatches && startFields) {
    if (startScheduler) {
      startScheduler = { ...startScheduler, ...sharedFields, ...startFields };
    }
  } else if (type === 'start' || type === 'both') {
    startScheduler = { ...startScheduler, ...sharedFields };
  }

  if (hasPerSidePatches && endFields) {
    if (endScheduler) {
      endScheduler = { ...endScheduler, ...sharedFields, ...endFields };
    }
  } else if (type === 'end' || type === 'both') {
    endScheduler = { ...endScheduler, ...sharedFields };
  }

  return { ...draft, startScheduler, endScheduler };
}

function clearScheduleDraft(draft: ScheduleSidePanelDraft, action: ClearScheduleAction): ScheduleSidePanelDraft {
  const { type } = action.payload;

  if (type === 'start') {
    return { ...draft, startScheduler: undefined };
  }
  if (type === 'end') {
    return { ...draft, endScheduler: undefined };
  }
  if (type === 'both') {
    return { ...draft, startScheduler: undefined, endScheduler: undefined };
  }

  return draft;
}

function saveContextualSchedulerDraft(
  draft: ScheduleSidePanelDraft,
  action: SaveContextualSchedulerAction,
): ScheduleSidePanelDraft {
  const { type, ...payload } = action.payload;
  let { startScheduler, endScheduler } = draft;

  if (['start', 'both'].includes(type)) {
    startScheduler = { ...startScheduler, ...payload };
  }
  if (['end', 'both'].includes(type)) {
    endScheduler = { ...endScheduler, ...payload };
  }

  return { ...draft, startScheduler, endScheduler };
}

function updateStepDraft(draft: ScheduleSidePanelDraft, action: UpdateStepAction): ScheduleSidePanelDraft {
  const { fields } = action.payload;
  return {
    ...draft,
    step: { ...draft.step, ...fields },
  };
}

function updateRuleSimpleDraft(draft: ScheduleSidePanelDraft, action: UpdateRuleAction): ScheduleSidePanelDraft {
  const { refId, fields } = action.payload;

  if (refId !== draft.rule.refId) {
    return draft;
  }

  return {
    ...draft,
    rule: {
      ...draft.rule,
      ...fields,
    },
  };
}

export function scheduleDraftReducer(
  draft: ScheduleSidePanelDraft,
  action: ScheduleDraftAction,
): ScheduleSidePanelDraft {
  switch (action.type) {
    case 'SEED_SCHEDULE_DRAFT':
      return (action.payload as { draft: ScheduleSidePanelDraft }).draft;
    case 'UPDATE_SCHEDULE':
      return upsertScheduleDraft(draft, action as UpsertScheduleAction);
    case 'CLEAR_SCHEDULE':
      return clearScheduleDraft(draft, action as ClearScheduleAction);
    case 'SAVE_CONTEXTUAL_SCHEDULER':
      return saveContextualSchedulerDraft(draft, action as SaveContextualSchedulerAction);
    case 'UPDATE_STEP':
      return updateStepDraft(draft, action as UpdateStepAction);
    case 'UPDATE_RULE_SIMPLE':
      return updateRuleSimpleDraft(draft, action as UpdateRuleAction);
    default:
      return draft;
  }
}

export function buildScheduleSavePayload(
  draft: ScheduleSidePanelDraft,
  stepIdx: number,
  ruleRefId?: string,
): SaveStepScheduleAction['payload'] {
  const { scheduleBasedOn, ...stepFields } = draft.step;
  // 'time' is the only mode that evaluates the absolute start/end schedule; 'events' relies on
  // predicates and 'nothing' means the schedule is turned off entirely.
  const isScheduleOff = scheduleBasedOn === 'nothing';
  const omitSched = isScheduleOff
    ? true
    : scheduleBasedOn === undefined
    ? stepFields.omitSched ?? true
    : stepFields.omitSched ?? scheduleBasedOn !== 'time';

  const payload: SaveStepScheduleAction['payload'] = {
    stepIdx,
    step: {
      ...stepFields,
      ...(isScheduleOff ? { omitStart: true, omitEnd: true } : {}),
      omitSched,
      isDirty: true,
    },
    startScheduler:
      !isScheduleOff && draft.startScheduler && Object.keys(draft.startScheduler).length > 0
        ? structuredClone(draft.startScheduler)
        : undefined,
    endScheduler:
      !isScheduleOff && draft.endScheduler && Object.keys(draft.endScheduler).length > 0
        ? structuredClone(draft.endScheduler)
        : undefined,
  };

  if (ruleRefId && draft.rule.evaluateAtAbsoluteIntervalStart !== undefined) {
    payload.ruleRefId = ruleRefId;
    payload.ruleFields = { evaluateAtAbsoluteIntervalStart: draft.rule.evaluateAtAbsoluteIntervalStart };
  }

  return payload;
}

export function selectSchedulesFromDraft(draft: ScheduleSidePanelDraft) {
  return {
    start: draft.startScheduler
      ? {
          value: draft.startScheduler.start ?? 0,
          timezone: draft.startScheduler.timezone?.name ?? '',
          cronExpression: draft.startScheduler.cronExpression ?? '',
        }
      : undefined,
    end: draft.endScheduler
      ? {
          value: (draft.endScheduler.start as number) ?? 0,
          timezone: draft.endScheduler.timezone?.name ?? '',
          cronExpression: draft.endScheduler.cronExpression ?? '',
        }
      : undefined,
  };
}
